import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Bot } from "lucide-react";
import { logJourney } from "@/lib/journey";
import "./IPOCostEstimator.css";

const API = process.env.REACT_APP_BACKEND_URL;

const mk = (a) => ({ icon: a[0], icon_style: a[1], role: a[2], note: a[3], amount: a[4], type: a[5], type_label: a[6] });

// SME calculator model — faithful to the "SME IPO cost estimator.xlsx" (amounts in ₹ Lakhs).
// [icon, icon_style, role, note, fixed_low, fixed_high, pct_low, pct_high, gst, refundable, type, expense_type]
const smk = (a) => ({ icon: a[0], icon_style: a[1], role: a[2], note: a[3], fixed_low: a[4], fixed_high: a[5], pct_low: a[6], pct_high: a[7], gst: a[8], refundable: a[9], type: a[10], expense_type: a[11] });
const P = "#eef0fb;color:#3C3489", G = "#e1f5ee;color:#085041", B = "#e6f1fb;color:#0c447c", O = "#fef0e6;color:#a8560f";
const SME_MODEL = [
  ["ti-clipboard-check", P, "IPO Advisory — CA Firm / Consultant", "Pre-IPO health check, financial restructuring, process coordination", 3, 10, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-briefcase", P, "Merchant Banker (BRLM)", "Lead management, DRHP/RHP drafting, filings, pricing, marketing", 15, 40, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-users", O, "Underwriters / Syndicate Members", "Underwriting commitment (often the BRLM itself for SME)", 0, 0, 0.01, 0.02, true, false, "t-var", "% of Issue Size"],
  ["ti-scale", P, "Legal Advisor", "Legal due diligence, agreement drafting, professional fees", 3, 8, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-calculator", P, "Statutory Auditor", "Audit fee for restated financials, CA certificates", 3, 8, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-file-certificate", P, "External CS (PCS)", "Practising CS certificates, secretarial compliance", 1, 3, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-id", P, "Registrar to the Issue (RTA)", "Application processing, allotment, refunds, demat credit", 1, 3, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-id", G, "NSDL / CDSL", "Depository admission/joining fee, corporate action & ISIN charges", 1, 2, 0, 0, true, false, "t-reg", "Fixed + Statutory"],
  ["ti-building-bank", P, "Bankers to the Issue / ASBA", "Escrow account operation, ASBA processing charges", 0.5, 2, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-building-bank", B, "SEBI Fees", "SME: Nil (filed with exchange, not SEBI) | Mainboard: per ICDR fee schedule", 0, 0, 0, 0, false, false, "t-reg", "Statutory"],
  ["ti-file-text", G, "ROC / MCA Filings", "Conversion to public co., allotment filings (PAS-3 etc.), ROC charges", 1, 3, 0, 0, false, false, "t-reg", "Statutory + Fixed"],
  ["ti-stamp", G, "Stamp Duty", "On BRLM, underwriting, escrow & RTA agreements (state-dependent)", 1, 5, 0, 0, false, false, "t-reg", "Statutory"],
  ["ti-device-laptop", P, "SDD Software", "Structured Digital Database for insider-trading tracking — SEBI (PIT) Regs, 2015, Reg. 3(5)", 0.5, 2, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-speakerphone", O, "Newspaper Advertisement", "Public notice for DRHP/RHP filing, pre- & post-issue ads", 3, 8, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-businessplan", O, "Market Maker — Fee", "Mandatory 3-yr market-making commitment (SEBI ICDR Regs)", 5, 15, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-package", O, "Market Maker — Inventory", "Market-making inventory held (returns over the 3-yr commitment)", 0, 0, 0.05, 0.05, false, true, "t-var", "% of Issue Size"],
  ["ti-building-store", B, "Exchange Listing — Processing Fee", "Listing application processing charges", 1, 3, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-building-store", B, "Exchange Listing — Refundable Deposit", "1% NOC deposit, refundable on compliance", 0, 0, 0.01, 0.01, false, true, "t-var", "% of Issue Size"],
  ["ti-printer", O, "Printing & Stationery", "DRHP/RHP, application forms, abridged prospectus", 2, 5, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-ad", O, "Marketing, PR & Roadshow", "Investor roadshows, digital/PR campaign", 3, 10, 0, 0, true, false, "t-fixed", "Fixed"],
  ["ti-dots-circle-horizontal", O, "Miscellaneous / Contingency", "Travel, courier, out-of-pocket, unforeseen", 2, 5, 0, 0, true, false, "t-fixed", "Fixed"],
].map(smk);

const MB_ROWS = [
  ["ti-briefcase", "#eef0fb;color:#3C3489", "Merchant Bankers (BRLMs)", "Lead management, ~1–3% of issue · often multiple", "₹2–6 Cr", "t-fixed", "Largest"],
  ["ti-scale", "#eef0fb;color:#3C3489", "Legal Counsel", "Domestic + international due diligence", "₹40–90 L", "t-fixed", "Fixed"],
  ["ti-calculator", "#eef0fb;color:#3C3489", "Statutory & Peer Auditor", "Restated financials, comfort letters", "₹30–60 L", "t-fixed", "Fixed"],
  ["ti-building-bank", "#e6f1fb;color:#0c447c", "SEBI Filing Fee", "0.005%–0.05% of issue size (slab)", "₹5–25 L", "t-reg", "Regulatory"],
  ["ti-id", "#e1f5ee;color:#085041", "Registrar (RTA)", "Applications, allotment, refunds", "₹15–35 L", "t-fixed", "Fixed"],
  ["ti-file-certificate", "#eef0fb;color:#3C3489", "Company Secretary", "Compliance, ROC, corporate actions", "₹10–25 L", "t-fixed", "Fixed"],
  ["ti-speakerphone", "#fef0e6;color:#a8560f", "Marketing, PR & Roadshow", "Advertising, investor outreach, IR", "₹50 L–2 Cr", "t-var", "Variable"],
  ["ti-printer", "#fef0e6;color:#a8560f", "Printing & Distribution", "Prospectus, forms, dispatch", "₹20–50 L", "t-var", "Variable"],
  ["ti-users", "#fef0e6;color:#a8560f", "Underwriting / Syndicate", "% of issue (if underwritten)", "₹40 L–1.5 Cr", "t-var", "Variable"],
  ["ti-id", "#e1f5ee;color:#085041", "Depositories (NSDL/CDSL)", "Admission & corporate action", "₹5–12 L", "t-reg", "Regulatory"],
  ["ti-building-skyscraper", "#e6f1fb;color:#0c447c", "Exchange Listing Fees", "Initial + processing (NSE/BSE)", "₹10–25 L", "t-reg", "Regulatory"],
  ["ti-shield-check", "#eef0fb;color:#3C3489", "Rating / Monitoring Agency", "Credit rating + monitoring agency", "₹15–40 L", "t-fixed", "Fixed"],
].map(mk);

const MB_META = { applicant: "NSE / BSE Main Board applicant", range: "₹5–15 Cr", issue: "₹100 Cr+", total: "₹5–15 Cr (issue-size dependent)",
  meta: "Modelled from public Main Board data. BRLM fees are the largest component. Benchmark: 2–5% of issue size." };

const fmt = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
};
const rangeL = (lo, hi) => (Math.abs(lo - hi) < 0.005 ? `₹${fmt(lo)} L` : `₹${fmt(lo)} – ${fmt(hi)} L`);

export default function IPOCostEstimator() {
  const navigate = useNavigate();
  const [listing, setListing] = useState("sme");
  // SME calculator state
  const [issueLakhs, setIssueLakhs] = useState(3000);
  const [smeRows, setSmeRows] = useState(SME_MODEL.map((r) => ({ ...r })));
  // Main Board (unchanged) state
  const [rows, setRows] = useState(MB_ROWS.map((r) => ({ ...r })));
  const [range, setRange] = useState(MB_META.range);
  const [issue, setIssue] = useState(MB_META.issue);
  const [meta, setMeta] = useState(MB_META.meta);
  const [total, setTotal] = useState(MB_META.total);

  const [editing, setEditing] = useState(false);
  const [versions, setVersions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [workspaceReady, setWorkspaceReady] = useState(true);
  const [saving, setSaving] = useState(false);

  // Live SME computation
  const calc = useMemo(() => {
    let subL = 0, subH = 0, gstBaseL = 0, gstBaseH = 0, refL = 0, refH = 0;
    const out = smeRows.map((r) => {
      const el = (Number(r.fixed_low) || 0) + (Number(r.pct_low) || 0) * issueLakhs;
      const eh = (Number(r.fixed_high) || 0) + (Number(r.pct_high) || 0) * issueLakhs;
      subL += el; subH += eh;
      if (r.gst) { gstBaseL += el; gstBaseH += eh; }
      if (r.refundable) { refL += el; refH += eh; }
      return { ...r, est_low: el, est_high: eh };
    });
    const gstL = 0.18 * gstBaseL, gstH = 0.18 * gstBaseH;
    const totL = subL + gstL, totH = subH + gstH;
    return {
      out, subL, subH, gstL, gstH, totL, totH,
      refL, refH, netL: totL - refL, netH: totH - refH,
      pctL: issueLakhs ? (totL / issueLakhs) * 100 : 0,
      pctH: issueLakhs ? (totH / issueLakhs) * 100 : 0,
    };
  }, [smeRows, issueLakhs]);
  const inBand = issueLakhs >= 2000 && issueLakhs <= 6000;

  const loadDoc = useCallback((d) => {
    const lt = d.listing_type || "sme";
    setListing(lt);
    setCurrentId(d.id);
    if (lt === "sme") {
      setIssueLakhs(d.issue_size_lakhs || 3000);
      // Row structure (role/note/type/expense_type/pct/gst/refundable) always comes
      // from the current model; only the user-editable fixed_low/high are restored.
      const saved = Array.isArray(d.rows) ? d.rows : [];
      setSmeRows(SME_MODEL.map((base, idx) => {
        const s = saved[idx];
        return {
          ...base,
          fixed_low: s && s.fixed_low !== undefined ? s.fixed_low : base.fixed_low,
          fixed_high: s && s.fixed_high !== undefined ? s.fixed_high : base.fixed_high,
        };
      }));
    } else {
      setRows((d.rows || MB_ROWS).map((r) => ({ ...r })));
      setRange(d.range_label || MB_META.range);
      setIssue(d.issue_size_label || MB_META.issue);
      setMeta(d.meta || MB_META.meta);
      setTotal(d.total_label || MB_META.total);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/cost-estimator`, { credentials: "include" });
      const d = await r.json();
      setVersions(d.versions || []);
      setWorkspaceReady(!!d.workspace_ready);
      if (d.current) loadDoc(d.current);
    } catch { /* keep defaults */ }
  }, [loadDoc]);

  useEffect(() => { refresh(); logJourney("IPO Cost Estimator", "Viewed", "Opened IPO Cost Estimator"); }, [refresh]);

  const pick = (w) => {
    setListing(w);
    setCurrentId(null);
    setEditing(false);
    if (w === "sme") {
      setIssueLakhs(3000);
      setSmeRows(SME_MODEL.map((r) => ({ ...r })));
    } else {
      setRows(MB_ROWS.map((r) => ({ ...r })));
      setRange(MB_META.range); setIssue(MB_META.issue); setMeta(MB_META.meta); setTotal(MB_META.total);
    }
  };

  // Main Board editing helpers
  const setRow = (i, k, v) => setRows((s) => s.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((s) => [...s, mk(["ti-circle-plus", "#eef0fb;color:#3C3489", "New cost head", "Describe this cost", "₹0", "t-fixed", "Fixed"])]);
  const delRow = (i) => setRows((s) => s.filter((_, j) => j !== i));
  // SME editing (fixed low/high only)
  const setSme = (i, k, v) => setSmeRows((s) => s.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const payload = () => {
    if (listing === "sme") {
      const rl = rangeL(calc.totL, calc.totH);
      return {
        listing_type: "sme", mode: "calc", issue_size_lakhs: issueLakhs,
        applicant_label: "NSE Emerge / BSE SME applicant",
        issue_size_label: `₹${fmt(issueLakhs)} L (₹${fmt(issueLakhs / 100)} Cr)`,
        range_label: rl, total_label: rl,
        meta: "Calibrated for SME issues of ₹20–60 Cr. GST @18% on applicable professional fees.",
        rows: smeRows,
      };
    }
    return { listing_type: "mb", mode: "table", applicant_label: MB_META.applicant, range_label: range, issue_size_label: issue, meta, total_label: total, rows };
  };

  const saveNew = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/cost-estimator`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...payload(), name: `Estimate v${(versions[0]?.version || 0) + 1}` }) });
      const d = await r.json();
      if (r.ok) { toast.success("Estimate saved to your IPO Workspace"); await refresh(); setCurrentId(d.estimate.id); setEditing(false); }
      else { toast.error(d.detail || "Could not save"); }
    } catch { toast.error("Connection error"); }
    setSaving(false);
  };
  const saveChanges = async () => {
    if (!currentId) return saveNew();
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/cost-estimator/${currentId}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload()) });
      const d = await r.json();
      if (r.ok) { toast.success("Changes saved"); await refresh(); setEditing(false); }
      else { toast.error(d.detail || "Could not save"); }
    } catch { toast.error("Connection error"); }
    setSaving(false);
  };
  const onVersion = async (val) => {
    if (val === "__new") { pick(listing); return; }
    try { const r = await fetch(`${API}/api/cost-estimator/${val}`, { credentials: "include" }); if (r.ok) { loadDoc(await r.json()); setEditing(false); } } catch { /* ignore */ }
  };

  const downloadPdf = async () => {
    try {
      toast.info("Preparing your PDF…");
      const r = await fetch(`${API}/api/cost-estimator/export-pdf`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload()) });
      if (!r.ok) { toast.error("Could not generate PDF"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `IPO_Cost_Estimate_${listing.toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      logJourney("IPO Cost Estimator", "Exported PDF", "Downloaded IPO cost estimate PDF");
    } catch { toast.error("Connection error"); }
  };

  return (
    <div className="es" data-testid="ipo-cost-estimator-page">
      <div className="top">
        <div className="brand" onClick={() => navigate("/home")}>IPO<span>Labs</span></div>
        <button className="hdr-btn" onClick={() => navigate("/home")} data-testid="est-hdr-home"><Home size={15} strokeWidth={2.2} /> Home Page</button>
        <button className="hdr-btn oracle" onClick={() => navigate("/oracle")} data-testid="est-hdr-oracle"><Bot size={15} strokeWidth={2.2} /> Ask Oracle {"{IPO.GPT}"}</button>
        <div className="top-actions">
          {versions.length > 0 && (
            <select className="ver-sel" value={currentId || "__new"} onChange={(e) => onVersion(e.target.value)} data-testid="est-version-select">
              <option value="__new">+ New estimate (template)</option>
              {versions.map((v) => (<option key={v.id} value={v.id}>{v.name} · {v.listing_type.toUpperCase()} {v.is_current ? "· current" : ""}</option>))}
            </select>
          )}
          <button className="tbtn" onClick={downloadPdf} data-testid="est-download-pdf"><i className="ti ti-download" /> Download PDF</button>
          <button className={`tbtn ${editing ? "on" : ""}`} onClick={() => setEditing((e) => !e)} data-testid="est-edit-toggle">{editing ? "Done editing" : "✎ Edit figures"}</button>
          {currentId && <button className="tbtn" disabled={saving} onClick={saveChanges} data-testid="est-save-changes">Save changes</button>}
          <button className="tbtn primary" disabled={saving} onClick={saveNew} data-testid="est-save-new">{currentId ? "Save as new version" : "Save estimate"}</button>
          <div className="back" onClick={() => navigate(-1)} data-testid="est-back">← Back</div>
        </div>
      </div>

      <div className="hero">
        <div className="hero-k">Free tool · saved to your workspace</div>
        <div className="hero-h">Estimate your IPO cost</div>
        <div className="hero-d">A realistic, role-by-role breakdown based on real Indian IPO expenses. Choose your path — figures are indicative and adjust with issue size and readiness.</div>
      </div>

      <div className="toggle">
        <div className={`tg ${listing === "sme" ? "on" : ""}`} onClick={() => pick("sme")} data-testid="est-toggle-sme"><div className="tg-h"><i className="ti ti-building-store" aria-hidden="true" /> SME IPO</div><div className="tg-d">NSE Emerge / BSE SME</div></div>
        <div className={`tg ${listing === "mb" ? "on" : ""}`} onClick={() => pick("mb")} data-testid="est-toggle-mb"><div className="tg-h"><i className="ti ti-building-skyscraper" aria-hidden="true" /> Main Board IPO</div><div className="tg-d">NSE / BSE main board</div></div>
      </div>

      {listing === "sme" ? (
        <>
          <div className="issz" data-testid="sme-issue-card">
            <div className="issz-main">
              <label htmlFor="sme-iss">Issue Size <span>(₹ in Lakhs)</span></label>
              <div className="issz-row">
                <input id="sme-iss" type="number" min="0" step="100" value={issueLakhs}
                  onChange={(e) => setIssueLakhs(Math.max(0, Number(e.target.value) || 0))} data-testid="sme-issue-size-input" />
                <span className="issz-cr">= ₹{fmt(issueLakhs / 100)} Cr</span>
              </div>
              <div className="issz-hint">Enter your planned issue size (e.g. 3000 = ₹30 Cr). Every figure below recalculates automatically.</div>
            </div>
            <div className={`issz-band ${inBand ? "ok" : "warn"}`} data-testid="sme-band-indicator">
              <i className={`ti ${inBand ? "ti-circle-check" : "ti-alert-triangle"}`} />
              {inBand ? "Within calibrated ₹20–60 Cr band" : "Outside ₹20–60 Cr band — review fixed fees manually"}
            </div>
          </div>

          <div className="wrap">
            <div className="lbl"><i className="ti ti-list-details" aria-hidden="true" /> Start-to-end SME IPO cost <span className="src">↻ all figures in ₹ Lakhs · Est. cost updates with issue size</span></div>
            <div className="sme2-scroll">
              <div className="sme2" data-testid="est-cost-table">
                <div className="sme2-row head">
                  <div className="sr">#</div>
                  <div>Advisor / Role · Cost Head</div>
                  <div>Type of Expense</div>
                  <div className="c">Refund.</div>
                  <div className="r">Fixed Low</div>
                  <div className="r">Fixed High</div>
                  <div className="r">Est. Cost Low</div>
                  <div className="r">Est. Cost High</div>
                </div>
                {calc.out.map((r, i) => {
                  const isPct = !!(r.pct_low || r.pct_high);
                  return (
                    <div className="sme2-row" key={i} data-testid={`est-row-${i}`}>
                      <div className="sr">{i + 1}</div>
                      <div className="ch">
                        <div className="r-role">{r.role}</div>
                        <div className="r-note">{r.note}{isPct ? <span className="pct-basis"> · {fmt(r.pct_low * 100)}–{fmt(r.pct_high * 100)}% of issue size</span> : null}</div>
                      </div>
                      <div><span className={`r-type ${r.type}`}>{r.expense_type}</span></div>
                      <div className="c">{r.refundable ? <span className="yn y" data-testid={`est-refund-${i}`}>Y</span> : <span className="yn n" data-testid={`est-refund-${i}`}>N</span>}</div>
                      {editing ? (
                        <>
                          <div className="r"><input type="number" className="cell-inp num" value={r.fixed_low} onChange={(e) => setSme(i, "fixed_low", Number(e.target.value) || 0)} data-testid={`est-low-${i}`} /></div>
                          <div className="r"><input type="number" className="cell-inp num" value={r.fixed_high} onChange={(e) => setSme(i, "fixed_high", Number(e.target.value) || 0)} data-testid={`est-high-${i}`} /></div>
                        </>
                      ) : (
                        <>
                          <div className="r fx">{isPct ? "—" : fmt(r.fixed_low)}</div>
                          <div className="r fx">{isPct ? "—" : fmt(r.fixed_high)}</div>
                        </>
                      )}
                      <div className="r amt" data-testid={`est-amount-${i}`}>{fmt(r.est_low)}</div>
                      <div className="r amt" data-testid={`est-amount-high-${i}`}>{fmt(r.est_high)}</div>
                    </div>
                  );
                })}
                <div className="sme2-row foot" data-testid="sme-summary">
                  <div className="ft-lbl">Subtotal — Cost Items (before GST)</div>
                  <div className="r amt" data-testid="sme-subtotal">{fmt(calc.subL)}</div>
                  <div className="r amt" data-testid="sme-subtotal-high">{fmt(calc.subH)}</div>
                </div>
                <div className="sme2-row foot">
                  <div className="ft-lbl">Add: GST @ 18% on GST-applicable professional fees</div>
                  <div className="r amt" data-testid="sme-gst">{fmt(calc.gstL)}</div>
                  <div className="r amt" data-testid="sme-gst-high">{fmt(calc.gstH)}</div>
                </div>
                <div className="sme2-row foot total">
                  <div className="ft-lbl">TOTAL ISSUE COST (fixed and variable) (₹ in Lakhs)</div>
                  <div className="r amt" data-testid="sme-total">{fmt(calc.totL)}</div>
                  <div className="r amt" data-testid="sme-total-high">{fmt(calc.totH)}</div>
                </div>
                <div className="sme2-row foot muted">
                  <div className="ft-lbl">Total Issue Cost as % of Issue Size</div>
                  <div className="r amt" data-testid="sme-pct">{fmt(calc.pctL)}%</div>
                  <div className="r amt" data-testid="sme-pct-high">{fmt(calc.pctH)}%</div>
                </div>
                <div className="sme2-row foot muted">
                  <div className="ft-lbl">Memo: of which Refundable (Market-Maker inventory + Exchange deposit)</div>
                  <div className="r amt" data-testid="sme-refundable">{fmt(calc.refL)}</div>
                  <div className="r amt" data-testid="sme-refundable-high">{fmt(calc.refH)}</div>
                </div>
                <div className="sme2-row foot net">
                  <div className="ft-lbl">Net Cash Cost (excl. refundable deposits / inventory)</div>
                  <div className="r amt" data-testid="sme-net">{fmt(calc.netL)}</div>
                  <div className="r amt" data-testid="sme-net-high">{fmt(calc.netH)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sme-notes" data-testid="sme-notes">
            <div className="sn-h"><i className="ti ti-info-circle" aria-hidden="true" /> Notes &amp; sources</div>
            <div className="sn-band">Fixed-fee components are calibrated for an issue-size range of <b>₹20 Cr – ₹60 Cr (₹2,000 – ₹6,000 Lakhs)</b>. Outside this band, fixed fees may not scale linearly — please review manually.</div>
            <ol className="sn-list">
              <li>Enter your <b>Issue Size (₹ Lakhs)</b> above — it is the only input; all “% of Issue Size” items recalculate automatically.</li>
              <li><b>SEBI Fees:</b> SME issuers file the draft offer document with the exchange (BSE SME / NSE Emerge), not SEBI directly, so no SEBI filing fee applies — unlike mainboard issues.</li>
              <li><b>Refundable items:</b> Market-Maker inventory and the exchange’s 1% NOC deposit are refundable/returnable over time, not true P&amp;L expenses — see the Net Cash Cost row.</li>
              <li><b>GST:</b> applicability is indicative; confirm treatment for each vendor invoice with your GST advisor — some statutory / deposit items are outside GST.</li>
              <li><b>Fixed ranges</b> are calibrated for SME issue sizes of ₹20–60 Cr; for issues well outside this band, revisit the fixed components manually.</li>
            </ol>
            <div className="sn-q"><i className="ti ti-help-circle" aria-hidden="true" /> Questions on any line item? <span onClick={() => navigate("/oracle")} data-testid="sme-notes-oracle">Ask Oracle {"{IPO.GPT}"} →</span></div>
          </div>
        </>
      ) : (
        <>
          <div className="sumbar">
            <div className="sum-block"><div className="l">{MB_META.applicant}</div>
              {editing ? <input className="edit-inp" value={range} onChange={(e) => setRange(e.target.value)} data-testid="est-range-input" /> : <div className="v range" data-testid="est-range">{range}</div>}
            </div>
            <div className="sum-block"><div className="l">Typical issue size</div>
              {editing ? <input className="edit-inp" value={issue} onChange={(e) => setIssue(e.target.value)} /> : <div className="v">{issue}</div>}
            </div>
            <div className="sum-meta">{meta}</div>
          </div>

          <div className="wrap">
            <div className="lbl"><i className="ti ti-list-details" aria-hidden="true" /> Cost plan by role <span className="src">↻ modelled from public Main Board sources</span></div>
            <div className="ct" data-testid="est-cost-table">
              <div className="row head"><div /><div>Role / cost head</div><div>Amount (₹)</div><div>Type</div><div /></div>
              {rows.map((r, i) => (
                <div className="row" key={i} data-testid={`est-row-${i}`}>
                  <div className="r-ic" style={{ background: r.icon_style.split(";")[0], color: r.icon_style.split("color:")[1] }}><i className={`ti ${r.icon}`} aria-hidden="true" /></div>
                  {editing
                    ? <div><input className="cell-inp role" value={r.role} onChange={(e) => setRow(i, "role", e.target.value)} /><input className="cell-inp" style={{ marginTop: 4, fontSize: 10 }} value={r.note} onChange={(e) => setRow(i, "note", e.target.value)} /></div>
                    : <div><div className="r-role">{r.role}</div><div className="r-note">{r.note}</div></div>}
                  {editing
                    ? <input className="cell-inp" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} data-testid={`est-amount-${i}`} />
                    : <div className="r-amt" data-testid={`est-amount-${i}`}>{r.amount}</div>}
                  <div><span className={`r-type ${r.type}`}>{r.type_label}</span></div>
                  {editing ? <div className="del-x" onClick={() => delRow(i)} data-testid={`est-del-${i}`}>✕</div> : <div />}
                </div>
              ))}
              {editing && <div className="add-row" onClick={addRow} data-testid="est-add-row"><i className="ti ti-plus" /> Add cost head</div>}
              <div className="total-row">
                <div><i className="ti ti-sum" aria-hidden="true" /></div>
                <div>Estimated total IPO cost</div>
                {editing ? <input className="tv-inp" value={total} onChange={(e) => setTotal(e.target.value)} style={{ gridColumn: "3 / 5" }} data-testid="est-total-input" /> : <div className="tv" style={{ gridColumn: "3 / 5" }} data-testid="est-total">{total}</div>}
                <div />
              </div>
            </div>
          </div>
        </>
      )}

      {!workspaceReady && (
        <div className="save-hint" data-testid="est-workspace-hint">
          <i className="ti ti-info-circle" /> <span>Saving links this estimate to your <b>IPO Workspace</b>. Finish setting up your workspace to track it end-to-end.</span>
          <a onClick={() => navigate("/ipo-workspace-onboarding")} data-testid="est-setup-workspace">Set up my workspace →</a>
        </div>
      )}

      <div className="acts">
        <div className="act build" onClick={() => { logJourney("IPO Cost Estimator", "Build budget", "From estimator → Cost Tracker"); toast.success("Open the Cost Tracker in your IPO Navigator to build a live budget."); navigate("/ipo-navigator"); }} data-testid="est-build-budget">
          <div className="act-h"><i className="ti ti-pencil-plus" aria-hidden="true" /> Build my own budget</div>
          <div className="act-d">Turn this estimate into a live budget in your workspace — track every payment and sync to your IPO Navigator.</div>
          <button className="act-btn ab-w">Start my IPO budget →</button>
        </div>
        <div className="act fund" onClick={() => { logJourney("IPO Cost Estimator", "Funding enquiry", "From estimator"); navigate("/oracle"); }} data-testid="est-funding">
          <div className="act-h"><i className="ti ti-cash" aria-hidden="true" /> Need funding for this?</div>
          <div className="act-d">These costs are due before you list. Talk to us about bridge &amp; pre-IPO capital to fund the process.</div>
          <button className="act-btn ab-g">Contact us for funding →</button>
        </div>
      </div>

      <div className="note"><i className="ti ti-info-circle" style={{ fontSize: 12 }} aria-hidden="true" /> Figures are indicative, drawn from a real SME IPO expense ledger and public sources. Actual costs vary with issue size, exchange, intermediaries and negotiation — confirm with your merchant banker and CA.</div>
    </div>
  );
}
